import {
  BlobReader,
  ERR_ENCRYPTED,
  ERR_INVALID_PASSWORD,
  TextWriter,
  ZipReader
} from "@zip.js/zip.js";
import { ImportItem, JsonResultType } from "../types";
import { DEFAULT_BACKUP_ZIP_PASSWORD } from "../constants/backup";

const normalizeJson = (data: any): JsonResultType => {
  if (data?.account && data?.testeResult) {
    return data;
  }

  if (data?.account && Array.isArray(data?.quizAnswer) && data?.testResultId) {
    const accountId =
      data.account.accountId ?? data.account.patientId ?? null;

    return {
      account: {
        accountId,
        userId: data.account.userId,
        communication: data.account.communication ?? false,
        phone: data.account.phone ?? "",
        cpf: data.account.cpf,
        email: data.account.email ?? "",
        docType: data.account.docType ?? "CPF",
        birthDate: data.account.birthDate,
        nextLayout: data.account.nextLayout ?? 0,
        name: data.account.name,
        genre: data.account.genre ?? ""
      },
      testeResult: {
        testResultId: data.testResultId,
        anamneseResultId: data.anamneseResultId ?? "",
        quizAnswer: data.quizAnswer.map((answer: any) => ({
          rightAnswer: answer.rightAnswer,
          answer: answer.answer,
          quizId: answer.quizId,
          question: answer.question,
          endDateTime: answer.endDateTime,
          initDate: answer.initDate,
          userId: answer.userId,
          accountId: answer.accountId ?? accountId,
          location: answer.location,
          macAddress: answer.macAddress,
          professionalId: answer.professionalId ?? "",
          doctor: answer.doctor ?? null,
          email: answer.email ?? null,
          testResultId: answer.testResultId ?? data.testResultId,
          shots: answer.shots,
          otherAnswers: answer.otherAnswers ?? ""
        })),
        isNotSync: false,
        quizId: data.quizId,
        quizName: data.quizName ?? "",
        syncStatus: data.syncStatus ?? "",
        userId: data.userId,
        createdAt: data.createdAt,
        isDeleted: data.isDeleted ?? false
      }
    };
  }

  throw new Error("Formato JSON não reconhecido");
};

const validateJson = (data: JsonResultType): string[] => {
  const errors: string[] = [];

  if (!data.account) errors.push("Conta do paciente não encontrada");
  if (!data.account?.userId) errors.push("userId não informado");
  if (!data.account?.name?.trim()) errors.push("Nome do paciente não informado");
  if (!data.testeResult?.testResultId?.trim()) {
    errors.push("testResultId não informado");
  }
  if (!Array.isArray(data.testeResult?.quizAnswer)) {
    errors.push("Lista de respostas não encontrada");
  } else if (!data.testeResult.quizAnswer.length) {
    errors.push("O teste não possui respostas");
  }

  data.testeResult?.quizAnswer?.forEach((answer, index) => {
    if (!answer.testResultId) {
      errors.push(`Resposta ${index + 1} sem testResultId`);
    }
    if (!answer.quizId) errors.push(`Resposta ${index + 1} sem quizId`);
    if (answer.question === null || answer.question === undefined) {
      errors.push(`Resposta ${index + 1} sem número da pergunta`);
    }
  });

  return Array.from(new Set(errors));
};

const getDevice = (data?: JsonResultType): string =>
  data?.testeResult?.quizAnswer?.find(answer => answer.macAddress)?.macAddress ??
  "Não informado";

const parseJsonContent = (
  content: string,
  sourceArchive: string,
  sourceFile: string
): ImportItem[] => {
  try {
    const parsed = JSON.parse(content);
    const entries = Array.isArray(parsed) ? parsed : [parsed];

    return entries.map((entry, index) => {
      try {
        const data = normalizeJson(entry);
        const errors = validateJson(data);
        return {
          id: `${sourceArchive}:${sourceFile}:${index}`,
          sourceArchive,
          sourceFile:
            entries.length > 1 ? `${sourceFile} #${index + 1}` : sourceFile,
          device: getDevice(data),
          data,
          status: errors.length ? "INVALID" : "PENDING_VALIDATION",
          errors,
          warnings: []
        };
      } catch (error) {
        return {
          id: `${sourceArchive}:${sourceFile}:${index}`,
          sourceArchive,
          sourceFile,
          device: "Não informado",
          status: "INVALID",
          errors: [error instanceof Error ? error.message : "JSON inválido"],
          warnings: []
        };
      }
    });
  } catch (error) {
    return [
      {
        id: `${sourceArchive}:${sourceFile}:invalid`,
        sourceArchive,
        sourceFile,
        device: "Não informado",
        status: "INVALID",
        errors: ["Não foi possível interpretar o arquivo JSON"],
        warnings: []
      }
    ];
  }
};

const zipErrorMessage = (error: unknown): string => {
  const message = error instanceof Error ? error.message : String(error);
  if (message === ERR_INVALID_PASSWORD) return "Senha do backup incorreta";
  if (message === ERR_ENCRYPTED) return "Este backup está protegido. Informe a senha";
  return `Não foi possível abrir o ZIP: ${message}`;
};

const parseZip = async (
  file: File,
  password: string
): Promise<ImportItem[]> => {
  const zipReader = new ZipReader(new BlobReader(file));
  try {
    const entries = await zipReader.getEntries();
    const jsonEntries = entries.filter(
      entry =>
        !entry.directory &&
        entry.filename.toLowerCase().endsWith(".json") &&
        !entry.filename.startsWith("__MACOSX/")
    );

    if (!jsonEntries.length) {
      return [
        {
          id: `${file.name}:empty`,
          sourceArchive: file.name,
          sourceFile: "Nenhum JSON",
          device: "Não informado",
          status: "INVALID",
          errors: ["O ZIP não contém arquivos JSON"],
          warnings: []
        }
      ];
    }

    if (jsonEntries.some(entry => entry.encrypted) && !password) {
      throw new Error(ERR_ENCRYPTED);
    }

    const contents = await Promise.all(
      jsonEntries.map(async entry => {
        if (entry.directory) throw new Error("Entrada ZIP inválida");
        return {
          name: entry.filename,
          content: await entry.getData(
            new TextWriter(),
            entry.encrypted ? { password } : undefined
          )
        };
      })
    );

    return contents.flatMap(entry =>
      parseJsonContent(entry.content, file.name, entry.name)
    );
  } catch (error) {
    throw new Error(zipErrorMessage(error));
  } finally {
    await zipReader.close();
  }
};

export const loadImportFiles = async (
  files: File[],
  zipPassword = DEFAULT_BACKUP_ZIP_PASSWORD
): Promise<ImportItem[]> => {
  const groups = await Promise.all(
    files.map(async file => {
      const lowerName = file.name.toLowerCase();
      if (lowerName.endsWith(".zip")) return parseZip(file, zipPassword);
      if (lowerName.endsWith(".json")) {
        return parseJsonContent(await file.text(), "Arquivo avulso", file.name);
      }

      return [
        {
          id: `${file.name}:unsupported`,
          sourceArchive: file.name,
          sourceFile: file.name,
          device: "Não informado",
          status: "INVALID" as const,
          errors: ["Formato não suportado. Selecione JSON ou ZIP"],
          warnings: []
        }
      ];
    })
  );

  const items = groups.flat();
  const seenResults = new Set<string>();

  return items.map(item => {
    const testResultId = item.data?.testeResult?.testResultId?.toLowerCase();
    if (!testResultId || item.status === "INVALID") return item;

    if (seenResults.has(testResultId)) {
      return {
        ...item,
        status: "INVALID",
        errors: [...item.errors, "testResultId duplicado nos arquivos selecionados"]
      };
    }

    seenResults.add(testResultId);
    return item;
  });
};
